/**
 * Le coeur du toast « SMS reçu » : décider ce qui est nouveau.
 *
 * Isolé du composant parce que c'est la seule partie qui peut se tromper, et
 * la seule qui mérite un test : afficher l'historique au premier chargement
 * ferait surgir dix notifications à l'ouverture de l'écran, et rejouer un
 * message déjà vu en ferait surgir un à chaque interrogation du serveur.
 */

export interface SmsMessageLite {
  id?: string | null;
  direction?: string | null;
  from?: string | null;
  to?: string | null;
  body?: string | null;
  prospect_phone?: string | null;
  received_at?: string | null;
}

/** Les 9 derniers chiffres : ce qui rapproche +33616266395 de 06 16 26 63 95. */
export function phoneSuffix(phone?: string | null): string {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  return digits.length > 9 ? digits.slice(-9) : digits;
}

/**
 * Le SMS reçu le plus récent qui n'a pas encore été vu, ou null.
 * Seul l'entrant compte : un SMS qu'on vient d'envoyer n'est pas une nouvelle.
 */
export function pickNewInboundSms(
  messages: SmsMessageLite[] | undefined | null,
  seen: Set<string>,
): SmsMessageLite | null {
  if (!messages || messages.length === 0) return null;
  const candidates = messages.filter(
    (m) => m.direction === 'inbound' && !!m.id && !seen.has(String(m.id)),
  );
  if (candidates.length === 0) return null;
  return candidates.reduce((newest, m) => {
    const a = m.received_at ? new Date(m.received_at).getTime() : 0;
    const b = newest.received_at ? new Date(newest.received_at).getTime() : 0;
    return a > b ? m : newest;
  });
}

/**
 * La première ligne du message, pour la notification.
 *
 * 🚨 Kevin, 2026-09-30 : « le prénom et le nom et la première ligne ». Un SMS
 * peut en porter dix ; la notification en montre une, et le reste se lit dans
 * la fiche. Une ligne, c'est jusqu'au premier retour à la ligne, pas les deux
 * premières lignes rognées par le CSS.
 */
export function firstLine(body?: string | null): string {
  if (!body) return '';
  const ligne = String(body).split(/\r?\n/).find((l) => l.trim() !== '');
  return (ligne ?? '').trim();
}

/**
 * La conversation dans l'ordre d'une messagerie : le plus ancien en haut, le
 * plus récent en bas.
 *
 * 🚨 Kevin, 2026-09-30 : l'onglet SMS affichait l'inverse, parce que le
 * serveur rend les messages du plus récent au plus ancien pour les listes.
 * Une conversation ne se lit pas comme un fil d'actualité : on y descend.
 *
 * Le tri ne modifie jamais le tableau reçu, et deux messages de la même
 * seconde gardent l'ordre où ils sont arrivés.
 */
export function conversationOrder<T extends { timestamp?: string | null }>(messages: T[]): T[] {
  const instant = (valeur?: string | null) => {
    const t = valeur ? Date.parse(valeur) : NaN;
    return Number.isNaN(t) ? 0 : t;
  };
  return messages
    .map((message, rang) => ({ message, rang }))
    .sort((a, b) => instant(a.message.timestamp) - instant(b.message.timestamp) || a.rang - b.rang)
    .map(({ message }) => message);
}
